import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  message, 
  Space, 
  Typography, 
  Modal,
  Form,
  Input,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  ReloadOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { handleFetchResponse } from '../utils/authUtils';
import { API_ENDPOINTS } from '../utils/apiConfig';

const { Title } = Typography;

interface Team {
  _id: string;
  code: string;
  createdAt: string;
  updatedAt: string;
}

const TeamManagement: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm();

  /**
   * Fetch teams from the API
   */
  const fetchTeams = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TEAMS_ALL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      await handleFetchResponse(
        response,
        (data: Team[]) => {
          setTeams(data);
        },
        (data) => {
          message.error(data.message || 'Failed to load teams');
        }
      );
    } catch (error) {
      console.error('Error fetching teams:', error);
      message.error('An error occurred while loading teams');
    } finally {
      setLoading(false);
    }
  };

  // Load teams on component mount
  useEffect(() => {
    fetchTeams();
  }, []);

  /**
   * Handle creating a new team
   */
  const handleCreateTeam = async (values: { code: string }) => {
    setCreateLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.TEAMS_ALL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });
      await handleFetchResponse(
        response,
        (data) => {
          message.success('Team created successfully');
          setCreateModalVisible(false);
          form.resetFields();
          fetchTeams();
        },
        (data) => {
          message.error(data.message || 'Failed to create team');
        }
      );
    } catch (error) {
      console.error('Error creating team:', error);
      message.error('An error occurred while creating team');
    } finally {
      setCreateLoading(false);
    }
  };

  /**
   * Handle modal cancel
   */
  const handleModalCancel = () => {
    setCreateModalVisible(false);
    form.resetFields();
  };

  // Table columns
  const columns = [
    {
      title: 'Team Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {code}
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TeamOutlined style={{ fontSize: '24px', marginRight: '12px', color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0 }}>
              Team Management
            </Title>
          </div>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchTeams}
              loading={loading}
            >
              Refresh
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setCreateModalVisible(true)}
            >
              Create Team
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={teams}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} teams`,
          }}
        />
      </Card>

      {/* Create Team Modal */}
      <Modal
        title="Create New Team"
        open={createModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTeam}
        >
          <Form.Item
            label="Team Code"
            name="code"
            rules={[
              { required: true, message: 'Please enter team code' },
              { min: 2, message: 'Team code must be at least 2 characters' },
              { max: 20, message: 'Team code must be at most 20 characters' },
            ]}
          >
            <Input 
              placeholder="Enter team code (e.g., TEAM-A, DEV-1)" 
              maxLength={20}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={createLoading}
              >
                Create Team
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamManagement; 